<template>
  <el-upload
    drag
    :action="action"
    :headers="uploadHeaders"
    :on-success="onSuccessUpload"
    :on-error="onFailedUpload"
    :on-progress="progressUpload"
    :before-upload="beforeUpload"
    :file-list="uploadFiles"
    :on-change="onChangeFileList"
    :on-remove="onChangeFileList"
    :before-remove="beforeRemove"
    :disabled="disabled"
    :show-file-list="showFileList"
    :accept="accept"
    multiple
  >
    <div :class="[disabled ? 'is-disabled' : '']">
      <template v-if="disabled">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">Upload is disabled at this stage</div>
      </template>
      <template v-else>
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          Drop file here or <em>click to upload</em>
        </div>
      </template>
    </div>
  </el-upload>
</template>

<script>
export default {
  name: 'multi-upload',

  props: {
    showFileList: {
      required: false,
      type: Boolean,
      default: true
    },

    action: {
      required: true,
      type: String
    },

    deleteAction: {
      required: true,
      type: String
    },

    disabled: {
      required: false,
      type: Boolean,
      default: false
    },

    accept: {
      required: false,
      type: String,
      default: null
    },

    maxFileSize: {
      required: false,
      type: Number,
      default: null
    },

    allowedExtensions: {
      required: false,
      type: Array,
      default: null
    },

    preUploadHook: {
      required: false,
      type: Function,
      default: null
    }
  },

  data() {
    return {
      uploadFiles: [],
      uploadPercentage: 0,
      uploadFileList: [],
      uploadStatus: 'success'
    }
  },

  methods: {
    onChangeFileList(file, fileList) {
      this.uploadFileList = fileList
      this.uploadStatus = 'success'
      this.uploadPercentage = 0
      this.$emit('change', this.uploadFileList)
    },

    onSuccessUpload() {
      this.$notify({
        offset: 95,
        title: 'File Upload',
        message: 'File has been uploaded successfully.',
        type: 'success',
        showClose: true
      })
      this.uploadStatus = 'success'
      this.$emit('success', true)
    },

    onFailedUpload(err) {
      let message = 'Something went wrong.'
      if (err.status === 413) {
        message = 'The file is too large. Maximum allowed size is 50MB.'
      } else {
        try {
          const json = JSON.parse(err.message)
          message = json.message
        } catch {}
      }

      this.$emit('failed', true)
      this.$notify({
        offset: 95,
        title: 'Error while uploading',
        message: message,
        type: 'error',
        showClose: true
      })
      this.uploadStatus = 'exception'
      this.uploadPercentage = 0
    },

    async beforeUpload(file) {
      if (this.allowedExtensions && this.allowedExtensions.length) {
        const extension = file.name.split('.').pop().toLowerCase()
        if (!this.allowedExtensions.includes(extension)) {
          this.$message.error(
            `The file "${file.name}" is not supported. Allowed formats: ${this.allowedExtensions.join(', ')}.`
          )
          return false
        }
      }

      if (this.maxFileSize && file.size > this.maxFileSize) {
        const maxSizeMB = Math.round(this.maxFileSize / (1024 * 1024))
        this.$message.error(
          `The file "${file.name}" exceeds the maximum size of ${maxSizeMB}MB.`
        )
        return false
      }

      if (this.preUploadHook) {
        await this.preUploadHook()
      }

      this.$emit('before-upload', true)
      this.uploadStatus = 'success'
      this.uploadPercentage = 0

      return true
    },

    progressUpload(event) {
      this.uploadPercentage = parseInt(event.percent)
    },

    deleteFile(uuid) {
      return axios
        .delete(this.deleteAction, {
          data: {
            uuid: uuid
          }
        })
        .then((res) => {
          this.$notify({
            offset: 95,
            title: 'File Delete',
            message: res.data.message,
            type: 'success',
            showClose: true
          })
        })
    },

    beforeRemove(file) {
      let file_uuid =
        file.uuid ||
        (file.response && file.response.file && file.response.file.uuid)
      if (!file_uuid) {
        return true
      }
      let file_name = file.name || file_uuid
      return this.$confirm(`Remove ${file_name} ?`).then(() => {
        return this.deleteFile(file_uuid)
      })
    }
  },

  watch: {
    files() {
      this.uploadFiles = this.files
    }
  }
}
</script>
